export type AccountingBackend = 'zoho' | 'tally';

export const ACCOUNTING_CONFIG = {
  // Generic endpoints (backend picks the right implementation)
  CONNECT_URL: '/api/accounting/connect',
  SYNC_ALL_URL: '/api/accounting/sync/all',
  BACK_SYNC_URL: '/api/accounting/back-sync',
  /** Admin Tally per-module sync: POST {MODULE_SYNC_BASE_URL}/{module}?dry_run=1 */
  MODULE_SYNC_BASE_URL: '/api/accounting/sync',

  // Entity posting endpoints
  POST_INVOICE_URL: '/api/invoices/post-to-accounting',
  POST_BILL_URL: '/api/bills/post-to-accounting',
  POST_CREDIT_NOTE_URL: '/api/credit-notes/post-to-accounting',
  POST_DEBIT_NOTE_URL: '/api/debit-notes/post-to-accounting',
  POST_JOURNAL_URL: '/api/journals/post-to-accounting',
} as const;

export function getPostButtonLabel(backend: AccountingBackend): string {
  return `Post to ${getBackendName(backend)}`;
}

export function getPostedStatusLabel(backend: AccountingBackend): string {
  return `Posted to ${getBackendName(backend)}`;
}

/** Display name of the accounting backend, e.g. for inline phrases. */
export function getBackendName(backend: AccountingBackend): string {
  return backend === 'tally' ? 'Tally' : 'Zoho';
}

export function getPushButtonLabel(backend: AccountingBackend): string {
  return `Push to ${getBackendName(backend)}`;
}

export function getUpdateButtonLabel(backend: AccountingBackend): string {
  return `Update to ${getBackendName(backend)}`;
}

export function getCreateAndPushLabel(backend: AccountingBackend): string {
  return `Create and push to ${getBackendName(backend)}`;
}

export function getEditAndPushLabel(backend: AccountingBackend): string {
  return `Edit and Push to ${getBackendName(backend)}`;
}

export function getPushWithoutAttachmentLabel(
  backend: AccountingBackend,
): string {
  return `Push to ${getBackendName(backend)} without attachment`;
}

export function getPushingWithoutAttachmentLabel(
  backend: AccountingBackend,
): string {
  return `Pushing to ${getBackendName(backend).toLowerCase()} without attachment`;
}

export function getUpdateAfterPushLabel(backend: AccountingBackend): string {
  return `Update After Pushed to ${getBackendName(backend)}`;
}

export function getDeleteAfterPushLabel(backend: AccountingBackend): string {
  return `Delete After Pushed To ${getBackendName(backend)}`;
}

export function getEditedBeforePushLabel(backend: AccountingBackend): string {
  return `Edited before Pushing to ${getBackendName(backend)}`;
}

export function getPushPermissionDeniedMessage(
  entity: string,
  backend: AccountingBackend = getStoredAccountingBackend(),
): string {
  return `You do not have permission to push ${entity} to ${getBackendName(backend)}`;
}

export function getUpdateAfterPostPermissionDeniedMessage(
  entity: string,
  backend: AccountingBackend = getStoredAccountingBackend(),
): string {
  return `You do not have permission to update ${entity} after posting to ${getBackendName(backend)}`;
}

export function getUpdateDocumentAfterPushPermissionDeniedMessage(
  entity: string,
  backend: AccountingBackend = getStoredAccountingBackend(),
): string {
  return `You do not have permission to update this ${entity} after it has been pushed to ${getBackendName(backend)}.`;
}

export function getDeleteAfterPushPermissionDeniedMessage(
  entity: string,
  backend: AccountingBackend = getStoredAccountingBackend(),
): string {
  return `You do not have permission to delete ${entity} after they are pushed to ${getBackendName(backend)}`;
}

export function getReviewRequestPushReason(
  entity: string,
  backend: AccountingBackend = getStoredAccountingBackend(),
): string {
  return `Requesting permission to push ${entity} to ${getBackendName(backend)}`;
}

export function getReviewRequestUpdateAfterPostReason(
  entity: string,
  backend: AccountingBackend = getStoredAccountingBackend(),
): string {
  return `Requesting permission to update ${entity} after posting to ${getBackendName(backend)}`;
}

export function getApproveAndPostQueriesMessage(
  backend: AccountingBackend = getStoredAccountingBackend(),
): string {
  return `resolve all queries before approving and posting to ${getBackendName(backend)}.`;
}

export function getCreatedInBackendSuccessMessage(
  entity: string,
  backend: AccountingBackend = getStoredAccountingBackend(),
): string {
  return `${entity} created in ${getBackendName(backend)} successfully`;
}

export function getUpdatedInBackendSuccessMessage(
  entity: string,
  backend: AccountingBackend = getStoredAccountingBackend(),
): string {
  return `${entity} updated in ${getBackendName(backend)} successfully`;
}

export function getFailedCreateInBackendMessage(
  entity: string,
  backend: AccountingBackend = getStoredAccountingBackend(),
): string {
  return `Failed to create ${entity} in ${getBackendName(backend)}`;
}

export function getFailedUpdateInBackendMessage(
  entity: string,
  backend: AccountingBackend = getStoredAccountingBackend(),
): string {
  return `Failed to update ${entity} in ${getBackendName(backend)}`;
}

export function getDeletedFromBackendSuccessMessage(
  entity: string,
  backend: AccountingBackend = getStoredAccountingBackend(),
): string {
  return `${entity} deleted from ${getBackendName(backend)} successfully`;
}

export function getUpdatedAndPostedSuccessMessage(
  entity: string,
  backend: AccountingBackend = getStoredAccountingBackend(),
): string {
  return `${entity} updated and posted to ${getBackendName(backend)} successfully`;
}

export function getFailedUpdateAndPostMessage(
  entity: string,
  backend: AccountingBackend = getStoredAccountingBackend(),
): string {
  return `Failed to update and post ${entity} to ${getBackendName(backend)}`;
}

export function getSaveAndPublishLabel(
  backend: AccountingBackend = getStoredAccountingBackend(),
): string {
  return backend === 'tally'
    ? getPostButtonLabel(backend)
    : 'Save and Publish';
}

/**
 * Read the active accounting backend from stored company data.
 * Reads localStorage directly to avoid coupling to the auth storage module.
 * Defaults to 'zoho' when unknown, so existing Zoho behaviour is unchanged.
 */
export function getStoredAccountingBackend(): AccountingBackend {
  try {
    const raw = localStorage.getItem('company_data');
    if (!raw) return 'zoho';
    const parsed = JSON.parse(raw) as {
      accountingBackend?: string;
      accounting_backend?: string;
    };
    const backend = parsed?.accountingBackend ?? parsed?.accounting_backend;
    return backend === 'tally' ? 'tally' : 'zoho';
  } catch {
    return 'zoho';
  }
}

/** True when the active tenant books through Tally. */
export function isTallyBackend(
  backend: AccountingBackend = getStoredAccountingBackend(),
): boolean {
  return backend === 'tally';
}

/** True when the active tenant books through Zoho. */
export function isZohoBackend(
  backend: AccountingBackend = getStoredAccountingBackend(),
): boolean {
  return backend === 'zoho';
}

/**
 * Optional nav/route restriction. Omit (or pass undefined) for surfaces that
 * work on both backends. When set, the item/route is visible only for that
 * backend — matching "never show Zoho chrome on Tally, and vice versa".
 */
export type AccountingBackendGate = AccountingBackend;

export function matchesAccountingBackendGate(
  gate: AccountingBackendGate | undefined,
  backend: AccountingBackend = getStoredAccountingBackend(),
): boolean {
  return gate === undefined || gate === backend;
}
